import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["innerHTML"]

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxCardIssueCondensed from "src/components/card/SfxCardIssueCondensed.vue"
import SfxEmptyPage from "src/components/empty/SfxEmptyPage.vue"
import SfxSkeleton from "src/components/special/SfxSkeleton.vue"
import router from "src/router"
import { useCacheStore } from "src/stores/cacheStore"
import { SFX_CARD_CONDENSED_HEIGHT } from "src/util/constants/configurations"
import { EmptyStates } from "src/util/constants/emptyStates"
import {
  FILTER_COLUMN_SEARCH,
  FILTER_COLUMN_VALUE,
  FILTER_ENTITY_UUID,
} from "src/util/constants/filters"
import { MissingEntityType } from "src/util/enums/entityType"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import {
  fetchIssuePriorityListData,
  fetchWorkDomainWorkspaceListData,
} from "src/util/functions/entityData"
import {
  getIssueCardData,
  getOpenCorrectiveWorkOnItemForIssue,
  setOverdueTime,
  sortOpenIssuesData,
} from "src/util/functions/issues"
import {
  showNotImplementedNotification,
  showWarningNotification,
} from "src/util/functions/notifications"
import { IssueCardData, IssuesOnItem } from "src/util/types/dataModels/issue"
import { CorrectiveWorkOnItem } from "src/util/types/dataModels/performedWork"
import { computed, onMounted, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogListIssueManager',
  props: {
  // The list should be an ordered list
  list: {
    type: Array as PropType<IssuesOnItem[]>,
    required: true,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()
const cacheStore = useCacheStore()
const { dialogRef, onDialogCancel } = useDialogPluginComponent()
const elementsSelected = ref<IssueCardData | undefined>(undefined)
const issuesList = ref<IssueCardData[]>([])
const correctiveWorkOnItem = ref<CorrectiveWorkOnItem | undefined>()
const loadingData = ref(true)

const leftButtonText = computed(() =>
  elementsSelected.value ? t("sfxButtons.btn_open_issue") : t("sfxButtons.btn_cancel")
)

const leftButtonFunction = computed(() => (elementsSelected.value ? goToIssue : onDialogCancel))

const rightButtonText = computed(() =>
  elementsSelected.value ? t("sfxButtons.btn_correct_issue") : t("sfxButtons.btn_to_issues")
)

//endregion

//region Lifecycle

onMounted(async () => {
  await fetchWorkDomainWorkspaceListData()
  await fetchIssuePriorityListData()

  const issueData = await Promise.all(
    props.list.map(async (issue) => {
      let issueData = cacheStore.issueCard.get(issue.id)

      if (!issueData) {
        issueData = await getIssueCardData(issue)
        cacheStore.issueCard.set(issue.id, issueData)
      }
      setOverdueTime(issueData)

      return issueData
    })
  )
  issuesList.value = issueData.sort(sortOpenIssuesData)

  loadingData.value = false
})

//endregion

//region Functions

async function goToIssue() {
  const issueId = elementsSelected.value?.issueData.id
  await router.push({ name: NamedManagerPath.ISSUE, params: { issueId } })
}

function goToIssueList() {
  router.push({
    name: NamedManagerPath.SELECT_ISSUE,
    // TODO: Filters needs to be updated
    // Non closed issues for the given item
    query: {
      [FILTER_COLUMN_SEARCH]: "item_area",
      [FILTER_ENTITY_UUID]: props.list[0].actionable_item,
      [FILTER_COLUMN_VALUE]: "",
    },
  })
}

async function clickCorrectIssue() {
  if (correctiveWorkOnItem.value) {
    const msg = t("notifications.corrective_work_already_exists", {
      correctiveWorkTitle: correctiveWorkOnItem.value?.title,
    })
    showWarningNotification(msg)
  } else {
    showNotImplementedNotification()
    // TODO: Add when we have manager mode corrective work
    // await router.push({
    //   name: NamedManagerPath.ADHOC_CORRECTIVE_WORK,
    //   params: { issueId: elementsSelected.value?.issueData.id },
    // })
  }
}

async function rightButtonFunction() {
  elementsSelected.value ? await clickCorrectIssue() : goToIssueList()
}

function issueClick(issueClicked: IssueCardData) {
  elementsSelected.value =
    elementsSelected.value?.issueData.id === issueClicked.issueData.id ? undefined : issueClicked
}

function determineClass(issue: IssueCardData) {
  return elementsSelected.value?.issueData.id === issue.issueData.id
    ? "sfx-card--selected"
    : undefined
}

//endregion

//region Setup Code

watch(
  () => elementsSelected.value,
  async () => {
    const issueId = elementsSelected.value?.issueData.id
    if (issueId) correctiveWorkOnItem.value = await getOpenCorrectiveWorkOnItemForIssue(issueId)
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin sfx-dialog-list" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "text-h3" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t("sfxDialogListIssue.title_open_issues_on_item")), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1" }, {
            default: _withCtx(() => [
              _createElementVNode("span", {
                innerHTML: _ctx.$t('sfxDialogListIssue.text_more_than_one_issue')
              }, null, 8, _hoisted_1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "sfx-dialog-list-elements" }, {
            default: _withCtx(() => [
              (loadingData.value)
                ? (_openBlock(), _createBlock(SfxSkeleton, {
                    key: 0,
                    height: _unref(SFX_CARD_CONDENSED_HEIGHT),
                    "no-of-cards": 3
                  }, null, 8, ["height"]))
                : (issuesList.value?.length === 0)
                  ? (_openBlock(), _createBlock(SfxEmptyPage, {
                      key: 1,
                      class: "q-py-xl",
                      missingEntity: _unref(EmptyStates).get(_unref(MissingEntityType).ISSUE_NOT_FOUND)
                    }, null, 8, ["missingEntity"]))
                  : (issuesList.value?.length > 0)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(issuesList.value, (issue, index) => {
                        return (_openBlock(), _createBlock(SfxCardIssueCondensed, {
                          key: index,
                          issue: issue,
                          onClick: ($event: any) => (issueClick(issue)),
                          class: _normalizeClass(determineClass(issue))
                        }, null, 8, ["issue", "onClick", "class"]))
                      }), 128))
                    : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_actions, { class: "q-px-md q-pb-md" }, {
            default: _withCtx(() => [
              _createVNode(SfxBtnOutline, {
                onClick: leftButtonFunction.value,
                label: leftButtonText.value,
                class: "col"
              }, null, 8, ["onClick", "label"]),
              _createVNode(SfxBtn, {
                onClick: rightButtonFunction,
                label: rightButtonText.value,
                class: "col"
              }, null, 8, ["label"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})